import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, HeartPulse } from "lucide-react";

/**
 * ConsentModal — shown at first login (or when the consent version changes).
 * Two SEPARATE unchecked opt-ins are required by design:
 *   1. Datenschutz                       → consent_privacy_beta
 *   2. Gesundheits- und Fitnessdaten     → consent_health_fitness
 *
 * Both must be ticked to enable the CTA button. The full consent text
 * (multi-language) plus its version string is submitted to the backend
 * for audit-safe logging.
 */
const COPY = {
  it: {
    title: "Solano Essence",
    subtitle: "Privacy & consenso",
    intro:
      "Solano Essence è il tuo coach nutrizionale personale. Per offrirti le funzioni dell'app — diario alimentare, obiettivi, analisi e coach AI — trattiamo alcuni dati personali.",
    dataIntro:
      "Durante l'uso, in base alle funzioni utilizzate, possono essere trattati dati personali, in particolare:",
    dataList: [
      "Nome e indirizzo e-mail",
      "Età, sesso, altezza e peso",
      "Dati su alimentazione e pasti",
      "Calorie, macronutrienti e valori nutrizionali",
      "Assunzione di acqua",
      "Dati di attività e allenamento",
      "Passi",
      "Dati del sonno",
      "Dati corporei e di progresso",
      "Foto dei pasti e dei progressi",
      "Dati tecnici necessari al funzionamento e all'analisi degli errori",
    ],
    health:
      "Alcune di queste informazioni possono costituire dati sanitari o di fitness.",
    scope:
      "I dati possono essere trattati solo nella misura necessaria per le funzioni utilizzate, il funzionamento e il miglioramento dell'app. I dati non possono essere venduti o trasferiti per scopi pubblicitari di terzi senza una base giuridica corrispondente.",
    consent1Title: "Privacy",
    consent1:
      "Ho letto l'informativa sulla privacy e acconsento al trattamento dei miei dati personali per l'utilizzo di Solano Essence.",
    consent2Title: "Dati sanitari e di fitness",
    consent2:
      "Acconsento espressamente al trattamento dei miei dati sanitari, alimentari, corporei e di fitness da me forniti, nella misura necessaria per le funzioni di Solano Essence che utilizzo.",
    cta: "Accetta e inizia",
    revoke:
      "Puoi revocare il consenso in qualsiasi momento con effetto per il futuro.",
    saved: "Consenso registrato. Benvenuto in Solano!",
    saveErr: "Impossibile salvare il consenso. Riprova.",
  },
  en: {
    title: "Solano Essence",
    subtitle: "Privacy & consent",
    intro:
      "Solano Essence is your personal nutrition coach. To provide the app's features — food diary, goals, insights and AI coach — we process certain personal data.",
    dataIntro:
      "Depending on the features you use, personal data may be processed, in particular:",
    dataList: [
      "Name and e-mail address",
      "Age, gender, height and weight",
      "Nutrition and meal data",
      "Calories, macros and nutritional values",
      "Water intake",
      "Activity and training data",
      "Steps",
      "Sleep data",
      "Body and progress data",
      "Meal and progress photos",
      "Technical data required for operation and error analysis",
    ],
    health:
      "Some of this information may constitute health or fitness data.",
    scope:
      "Data may only be processed to the extent required for the features used, operation and improvement of the app. Data must not be sold or forwarded for third-party advertising purposes without a corresponding legal basis.",
    consent1Title: "Privacy",
    consent1:
      "I have read the privacy information and consent to the processing of my personal data for the use of Solano Essence.",
    consent2Title: "Health and fitness data",
    consent2:
      "I expressly consent to the processing of the health, nutrition, body and fitness data provided by me, as required for the features of Solano Essence I use.",
    cta: "Accept & get started",
    revoke:
      "You can revoke your consent at any time with effect for the future.",
    saved: "Consent recorded. Welcome to Solano!",
    saveErr: "Could not save consent. Please try again.",
  },
  de: {
    title: "Solano Essence",
    subtitle: "Datenschutz & Einwilligung",
    intro:
      "Solano Essence ist dein persönlicher Ernährungs-Coach. Um dir die Funktionen der App bereitzustellen — Ernährungstagebuch, Ziele, Auswertungen und KI-Coach — verarbeiten wir bestimmte personenbezogene Daten.",
    dataIntro:
      "Während der Nutzung können — abhängig von den verwendeten Funktionen — personenbezogene Daten verarbeitet werden, insbesondere:",
    dataList: [
      "Name und E-Mail-Adresse",
      "Alter, Geschlecht, Größe und Gewicht",
      "Ernährungs- und Mahlzeitendaten",
      "Kalorien, Makros und Nährwerte",
      "Wasserzufuhr",
      "Aktivitäts- und Trainingsdaten",
      "Schritte",
      "Schlafdaten",
      "Körper- und Fortschrittsdaten",
      "Mahlzeiten- und Fortschrittsfotos",
      "technische Daten, die für Betrieb und Fehleranalyse notwendig sind",
    ],
    health:
      "Einige dieser Informationen können Gesundheits- oder Fitnessdaten darstellen.",
    scope:
      "Die Daten dürfen nur verarbeitet werden, soweit dies für die verwendeten Funktionen, den Betrieb und die Verbesserung der App erforderlich ist. Die Daten dürfen nicht ohne entsprechende Rechtsgrundlage verkauft oder für fremde Werbezwecke weitergegeben werden.",
    consent1Title: "Datenschutz",
    consent1:
      "Ich habe die Datenschutzinformationen gelesen und stimme der Verarbeitung meiner personenbezogenen Daten für die Nutzung von Solano Essence zu.",
    consent2Title: "Gesundheits- und Fitnessdaten",
    consent2:
      "Ich willige ausdrücklich ein, dass meine von mir angegebenen Gesundheits-, Ernährungs-, Körper- und Fitnessdaten verarbeitet werden dürfen, soweit dies für die von mir genutzten Funktionen von Solano Essence erforderlich ist.",
    cta: "Zustimmen & loslegen",
    revoke:
      "Du kannst deine Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen.",
    saved: "Einwilligung gespeichert. Willkommen bei Solano!",
    saveErr: "Einwilligung konnte nicht gespeichert werden. Bitte erneut versuchen.",
  },
  fr: {
    title: "Solano Essence",
    subtitle: "Confidentialité & consentement",
    intro:
      "Solano Essence est ton coach nutrition personnel. Pour fournir les fonctionnalités de l'application — journal alimentaire, objectifs, analyses et coach IA — nous traitons certaines données personnelles.",
    dataIntro:
      "Selon les fonctionnalités utilisées, des données personnelles peuvent être traitées, notamment :",
    dataList: [
      "Nom et adresse e-mail",
      "Âge, sexe, taille et poids",
      "Données nutritionnelles et de repas",
      "Calories, macros et valeurs nutritionnelles",
      "Hydratation",
      "Données d'activité et d'entraînement",
      "Nombre de pas",
      "Données de sommeil",
      "Données corporelles et de progression",
      "Photos de repas et de progression",
      "Données techniques nécessaires au fonctionnement et à l'analyse d'erreurs",
    ],
    health:
      "Certaines de ces informations peuvent constituer des données de santé ou de fitness.",
    scope:
      "Les données ne peuvent être traitées que dans la mesure nécessaire aux fonctionnalités utilisées, au fonctionnement et à l'amélioration de l'application. Elles ne peuvent être vendues ni transmises à des fins publicitaires tierces sans base légale correspondante.",
    consent1Title: "Confidentialité",
    consent1:
      "J'ai lu les informations relatives à la confidentialité et je consens au traitement de mes données personnelles pour l'utilisation de Solano Essence.",
    consent2Title: "Données de santé et de fitness",
    consent2:
      "Je consens expressément au traitement de mes données de santé, nutritionnelles, corporelles et de fitness fournies, dans la mesure nécessaire aux fonctionnalités de Solano Essence que j'utilise.",
    cta: "Accepter et commencer",
    revoke:
      "Vous pouvez retirer votre consentement à tout moment avec effet pour l'avenir.",
    saved: "Consentement enregistré. Bienvenue chez Solano !",
    saveErr: "Impossible d'enregistrer le consentement. Veuillez réessayer.",
  },
};

export default function BetaConsentModal() {
  const { user, refresh } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const c = COPY[lang] || COPY.en;

  const [privacy, setPrivacy] = useState(false);
  const [health, setHealth] = useState(false);
  const [saving, setSaving] = useState(false);

  // Only show when user is logged in AND server flags consent as missing.
  const needsConsent = !!(user && user.consent && user.consent.needs_consent);

  // The login/register responses don't include the consent block (only
  // /auth/me does) — refresh once so version changes are detected right
  // after login instead of only after a full page reload.
  const consentUnknown = !!(user && user.consent === undefined);
  useEffect(() => {
    if (consentUnknown) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentUnknown]);

  // Nothing to render when consent is already up-to-date.
  useEffect(() => {
    if (!needsConsent) {
      setPrivacy(false);
      setHealth(false);
    }
  }, [needsConsent]);

  if (!needsConsent) return null;

  const submit = async () => {
    if (!privacy || !health) return;
    setSaving(true);
    try {
      await api.post("/privacy/consent", {
        privacy_beta: true,
        health_fitness: true,
        version: user.consent.current_version,
      });
      await refresh();
      toast.success(c.saved);
    } catch {
      toast.error(c.saveErr);
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = privacy && health && !saving;

  return (
    <Dialog open={needsConsent} onOpenChange={() => { /* not dismissible */ }}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 [&>button.absolute]:hidden z-[120]"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="beta-consent-modal"
      >
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-gradient-to-br from-[#FC9700]/8 to-[#5A8B8F]/8">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl md:text-3xl font-bold text-[#1A1D1E]">
              {c.title}
            </DialogTitle>
          </DialogHeader>
          <p className="mt-1 text-sm font-semibold text-[#5A8B8F]">{c.subtitle}</p>
        </div>

        <div className="px-6 py-5 space-y-4 text-sm text-slate-700 leading-relaxed">
          <p>{c.intro}</p>
          <div>
            <p className="mb-1">{c.dataIntro}</p>
            <ul className="list-disc pl-5 space-y-0.5">
              {c.dataList.map((it) => <li key={it}>{it}</li>)}
            </ul>
          </div>
          <p className="font-medium text-slate-800">{c.health}</p>
          <p>{c.scope}</p>
        </div>

        <div className="px-6 pb-4 space-y-3">
          <label
            className="flex items-start gap-3 p-3 rounded-2xl border border-slate-200 hover:border-[#5A8B8F]/40 cursor-pointer transition"
            data-testid="consent-privacy-label"
          >
            <Checkbox
              checked={privacy}
              onCheckedChange={(v) => setPrivacy(!!v)}
              className="mt-0.5 shrink-0"
              data-testid="consent-privacy-check"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1A1D1E] flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-[#5A8B8F]" />
                {c.consent1Title}
              </p>
              <p className="text-xs text-slate-600 mt-1">{c.consent1}</p>
            </div>
          </label>

          <label
            className="flex items-start gap-3 p-3 rounded-2xl border border-slate-200 hover:border-[#FC9700]/40 cursor-pointer transition"
            data-testid="consent-health-label"
          >
            <Checkbox
              checked={health}
              onCheckedChange={(v) => setHealth(!!v)}
              className="mt-0.5 shrink-0"
              data-testid="consent-health-check"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1A1D1E] flex items-center gap-1.5">
                <HeartPulse className="h-3.5 w-3.5 text-[#FC9700]" />
                {c.consent2Title}
              </p>
              <p className="text-xs text-slate-600 mt-1">{c.consent2}</p>
            </div>
          </label>
        </div>

        <div className="px-6 pb-6 pt-1 space-y-2 border-t border-slate-100">
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="consent-submit"
            className="w-full h-12 rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "…" : c.cta}
          </Button>
          <p className="text-xs text-slate-500 text-center">{c.revoke}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
